import React from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowLeft, CheckCircle2, Phone } from "lucide-react";
import SEO from "../components/SEO";
import SectionHeader from "../components/SectionHeader";
import { useSiteData, useLocaleText } from "../contexts/SiteDataContext";

const ServiceDetail = () => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { services, siteContent, loading } = useSiteData();
  const { pick, pickList } = useLocaleText();

  const service = (services || []).find((s) => s.slug === slug);
  const phones = siteContent?.phones || ["+974 50871458"];

  if (loading) {
    return <div className="py-20 text-center font-mono-rtc text-xs uppercase tracking-widest">Loading...</div>;
  }

  if (!service) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-20 text-center" data-testid="service-not-found">
        <h2 className="font-display text-3xl sm:text-4xl uppercase font-bold">Service Not Found</h2>
        <p className="mt-4 text-[#52525B]">The service you're looking for doesn't exist.</p>
        <Link to="/services" className="mt-8 inline-flex items-center gap-2 bg-[#FF5A00] text-white px-7 py-3.5 font-bold uppercase tracking-wide">
          <ArrowLeft size={16} /> Back to Services
        </Link>
      </div>
    );
  }

  const otherServices = (services || []).filter((s) => s.type === service.type && s.slug !== slug);
  const bullets = pickList(service.bullets);

  return (
    <div data-testid={`page-service-${slug}`}>
      <SEO title={pick(service.name)} description={pick(service.description)} path={`/services/${slug}`} image={service.image} />

      <section className="relative bg-[#111111] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={service.image} alt={pick(service.name)} className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#111111]/95 via-[#111111]/55 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-8 md:py-12">
          <Link to="/services" className="inline-flex items-center gap-2 font-mono-rtc text-[11px] uppercase tracking-[0.3em] text-[#FF5A00] hover:text-white transition-colors" data-testid="service-back-link">
            <ArrowLeft size={14} className="rtl:rotate-180" /> {t("common.backToServices")}
          </Link>
          <h1 className="font-display uppercase font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight mt-6">{pick(service.name)}</h1>
          <p className="mt-6 max-w-2xl text-lg text-white/80">{pick(service.short)}</p>
        </div>
      </section>

      <section className="py-10 md:py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 space-y-6">
            <SectionHeader eyebrow={t("common.scopeOfWork")} title={t("common.whatsIncluded")} />
            <p className="text-lg text-[#111111]/85 leading-relaxed">{pick(service.description)}</p>
            {bullets.length > 0 && (
              <ul className="mt-6 space-y-3">
                {bullets.map((b, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-[#111111]">
                    <CheckCircle2 size={20} className="text-[#FF5A00] mt-1 shrink-0" />
                    <span className="text-base">{b}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="pt-6 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" state={{ service: pick(service.name) }} className="inline-flex items-center justify-center gap-2 bg-[#FF5A00] text-white px-7 py-3.5 font-bold uppercase tracking-wide hover:bg-[#111111] transition-colors" data-testid="service-cta-quote">
                {t("common.requestQuote")} <ArrowRight size={16} className="rtl:rotate-180" />
              </Link>
              <a href={`tel:${phones[0].replace(/\s/g, "")}`} className="inline-flex items-center justify-center gap-2 border-2 border-[#111111] text-[#111111] px-7 py-3.5 font-bold uppercase tracking-wide hover:bg-[#111111] hover:text-white transition-colors" data-testid="service-cta-call">
                <Phone size={16} /> <span dir="ltr">{phones[0]}</span>
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative max-w-md mx-auto lg:max-w-none lg:ml-auto group">
              <div className="w-full aspect-[4/3] border-2 border-[#111111] group-hover:border-[#FF5A00] block-shadow-to-orange bg-gradient-to-br from-[#F8F8F8] to-[#EEEEEE] overflow-hidden flex items-center justify-center transition-all duration-300">
                <img src={service.image} alt={pick(service.name)} className="w-full h-full object-contain p-1 group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="absolute -top-5 -left-5 bg-[#FF5A00] text-[#111111] px-4 py-2 font-mono-rtc text-xs uppercase tracking-widest font-bold group-hover:bg-[#111111] group-hover:text-[#FF5A00] transition-colors duration-300">Service · Detail</div>
            </div>
          </div>
        </div>
      </section>

      {otherServices.length > 0 && (
        <section className="py-10 md:py-14 bg-[#FAFAFA] border-t border-[#E5E7EB]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-end justify-between mb-10">
              <h3 className="font-display uppercase font-bold text-xl sm:text-2xl md:text-3xl">
                {t("common.otherServices")}
              </h3>
              <Link to="/services" className="font-mono-rtc text-xs uppercase tracking-widest text-[#52525B] hover:text-[#FF5A00] link-underline">
                {t("common.viewAll")} →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {otherServices.slice(0, 3).map((s) => (
                <Link to={`/services/${s.slug}`} key={s.slug} className="group bg-white border-2 border-[#111111] p-5 md:p-6 hover:border-[#FF5A00] block-shadow-to-orange transition-all duration-300" data-testid={`related-service-${s.slug}`}>
                  <div className="aspect-[16/9] mb-4 overflow-hidden bg-[#F3F4F6]">
                    <img src={s.image} alt={pick(s.name)} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <h4 className="font-display text-2xl uppercase font-bold group-hover:text-[#FF5A00] transition-colors">{pick(s.name)}</h4>
                  <p className="mt-2 text-sm text-[#52525B]">{pick(s.short)}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ServiceDetail;
